'use client';

import { useState, useCallback, useRef } from 'react';
import type { Project, Lang } from '@inno-ck/shared';
import FolderNavigator from './FolderNavigator';
import MindmapCanvas from './MindmapCanvas';
import WhiteboardCanvas from './WhiteboardCanvas';
import TextDocCanvas from './TextDocCanvas';
import { getProject, setStorageMode } from '@/shell/services/api.service';
import {
  LOCAL_USER_ID,
  exportSnapshot,
  importSnapshot,
} from '@/shell/services/local-storage.service';
import { LangContext } from '@/shell/hooks';
import type { MindmapInstance } from '@/types';

/**
 * Free-tier shell — draw.io-style no-backend mode.
 *
 * Bootstraps the API service into 'local' storage mode (IndexedDB) before
 * any component renders. No auth, no server; everything lives in the browser.
 */

export default function FreeShell() {
  // Flip the dispatcher to local storage. Synchronous set so child useEffects
  // that fetch on mount see the right mode. React StrictMode double-invokes
  // effects in dev, so we DON'T reset to 'api' on cleanup — once in free mode,
  // staying in free mode until full page navigation is correct.
  setStorageMode('local');

  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [lang, setLang] = useState<Lang>('fr');
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const mindRef = useRef<MindmapInstance | null>(null);

  const handleOpenProject = useCallback(async (project: Project) => {
    mindRef.current = null;
    try {
      const fresh = await getProject(project.id);
      setCurrentProject(fresh || project);
    } catch {
      setCurrentProject(project);
    }
  }, []);

  const handleInstanceReady = useCallback((instance: MindmapInstance) => {
    mindRef.current = instance;
  }, []);

  const handleLogout = useCallback(() => {
    setCurrentProject(null);
  }, []);

  const [notice, setNotice] = useState<string | null>(null);

  const handleExport = useCallback(async () => {
    const snapshot = await exportSnapshot(LOCAL_USER_ID);
    const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const date = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `inno-ck-${date}.json`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
    setNotice(`Exporté : ${snapshot.folders.length} dossier(s), ${snapshot.projects.length} projet(s)`);
    setTimeout(() => setNotice(null), 3000);
  }, []);

  const handleImportWorkbook = useCallback(async (file: File) => {
    try {
      const text = await file.text();
      const snapshot = JSON.parse(text);
      await importSnapshot(snapshot, LOCAL_USER_ID);
      setNotice('Workbook importé — rechargement…');
      setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      setNotice(`Erreur : ${(err as Error).message}`);
      setTimeout(() => setNotice(null), 4000);
    }
  }, []);


  return (
    <LangContext.Provider value={lang}>
      <div className="flex h-screen overflow-hidden">
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/38 z-[900] hidden max-lg:block"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        <button
          className="hidden fixed top-2.5 left-2.5 z-[1100] bg-white border border-[#d8d8d8] rounded-lg w-[38px] h-[38px] text-lg leading-none cursor-pointer items-center justify-center shadow-md text-gray-800 transition-colors hover:bg-[#f0f0f0] max-lg:flex"
          onClick={() => setSidebarOpen(o => !o)}
          aria-label="Menu"
        >
          {sidebarOpen ? '✕' : '☰'}
        </button>

        {notice && (
          <div className="fixed top-14 right-3 z-[1000] bg-[#1e293b] text-white text-[12px] px-3 py-2 rounded shadow-lg">
            {notice}
          </div>
        )}

        <FolderNavigator
          userId={LOCAL_USER_ID}
          onOpenProject={(p: Project) => { handleOpenProject(p); setSidebarOpen(false); }}
          currentProject={currentProject}
          mindRef={mindRef}
          lang={lang}
          setLang={setLang}
          sidebarOpen={sidebarOpen}
          onLogout={handleLogout}
          freeMode
          onExportWorkbook={handleExport}
          onImportWorkbook={handleImportWorkbook}
        />
        {currentProject?.jsonData?.projectType === 'whiteboard'
          ? <WhiteboardCanvas project={currentProject} />
          : currentProject?.jsonData?.projectType === 'textdoc'
            ? <TextDocCanvas project={currentProject} />
            : <MindmapCanvas project={currentProject} onInstanceReady={handleInstanceReady} />
        }
      </div>
    </LangContext.Provider>
  );
}
